/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import API from '../api/api';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SavingsIcon from '@mui/icons-material/Savings';
import InventoryIcon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useI18n } from '../contexts/I18nContext';

const formatCurrency = (value, lang) => {
  if (value == null) return '-';
  return new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
};

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

export default function Finance() {
  const { showSnackbar } = useSnackbar();
  const { lang } = useI18n();
  const tr = (fr, ar) => (lang === 'ar' ? ar : fr);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(getCurrentMonth());
  const [tab, setTab] = useState(0);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await API.get('/finance/summary', { params: { month } });
      setSummary(res.data);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement', 'فشل التحميل'), 'error');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchSummary();
  }, [month]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      <Box
        sx={{
          mb: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          {tr('Finance & Inventaire', 'المالية والمخزون')}
        </Typography>
        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 520 }}>
          {tr("Vue mensuelle du chiffre d'affaires, du profit et de la valeur du stock.", 'عرض شهري للإيراد والربح وقيمة المخزون.')}
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 4,
          background: 'var(--surface-1)',
          border: '1px solid var(--border-subtle)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="month"
            label={tr('Mois', 'الشهر')}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 180 }}
          />
          <Tabs
            value={tab}
            onChange={(e, newValue) => setTab(newValue)}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              flex: 1,
              '& .MuiTab-root': { color: 'var(--text-muted)', fontWeight: 700 },
              '& .Mui-selected': { color: 'var(--text-primary)' },
            }}
          >
            <Tab label={tr('Vue generale', 'نظرة عامة')} />
            <Tab label={tr('Fournisseurs', 'الموردون')} />
          </Tabs>
        </Stack>
        <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700, mt: 2 }}>
          {summary?.profitNote || ''}
        </Typography>
      </Paper>

      {loading ? (
        <Stack alignItems="center">
          <CircularProgress sx={{ color: 'var(--text-primary)' }} />
        </Stack>
      ) : (
        <>
          {tab === 0 && (
            <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
            {[
              {
                label: tr("Chiffre d'affaires (mois)", 'الإيراد (شهري)'),
                value: formatCurrency(summary?.monthlyRevenue, lang),
                icon: <TrendingUpIcon />,
                accent: '#16a34a',
              },
              {
                label: tr('Profit brut (mois)', 'الربح الإجمالي (شهري)'),
                value: formatCurrency(summary?.monthlyProfit, lang),
                icon: <SavingsIcon />,
                accent: '#f97316',
              },
              {
                label: tr('Depenses (mois)', 'المصاريف (شهري)'),
                value: formatCurrency(summary?.monthlyExpenses, lang),
                icon: <ReceiptLongIcon />,
                accent: '#ef4444',
              },
              {
                label: tr('Profit net (mois)', 'الربح الصافي (شهري)'),
                value: formatCurrency(summary?.netProfit, lang),
                icon: <AccountBalanceWalletIcon />,
                accent: '#16a34a',
              },
            ].map((item) => (
              <Paper
                key={item.label}
                sx={{
                  flex: 1,
                  p: 3,
                  borderRadius: 4,
                  background:
                    'var(--surface-1)',
                  border: '1px solid var(--border-subtle)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `${item.accent}22`,
                    color: item.accent,
                    '& svg': { fontSize: 22 },
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'var(--text-primary)',
                      fontWeight: 800,
                      fontSize: isMobile ? 20 : 24,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>

          <Paper
            sx={{
              p: 3,
              mb: 2,
              borderRadius: 4,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(56,189,248,0.2)',
                  color: '#16a34a',
                }}
              >
                <InventoryIcon />
              </Box>
              <Box>
                <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                  {tr('Valeur inventaire', 'قيمة المخزون')}
                </Typography>
                <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: isMobile ? 20 : 24 }}>
                  {formatCurrency(summary?.inventoryValue, lang)}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
              {tr('Depenses par categorie', 'المصاريف حسب الفئة')}
            </Typography>
            {(summary?.expensesByCategory || []).length === 0 ? (
              <Typography sx={{ color: 'var(--text-muted)' }}>
                {tr('Aucune depense pour ce mois.', 'لا توجد مصاريف لهذا الشهر.')}
              </Typography>
            ) : (
              <Stack spacing={1.2}>
                {summary.expensesByCategory.map((item) => (
                  <Stack
                    key={item.category}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      background: 'var(--surface-3)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                      {item.category}
                    </Typography>
                    <Typography sx={{ color: 'var(--text-secondary)', fontWeight: 700 }}>
                      {formatCurrency(item.total, lang)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
            </>
          )}

          {tab === 1 && (
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                background: 'var(--surface-1)',
                border: '1px solid var(--border-subtle)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
                {tr('Resultats par fournisseur (mois)', 'نتائج حسب المورد (شهري)')}
              </Typography>
              <Box sx={{ mb: 3 }}>
                {(summary?.supplierStats || []).length === 0 ? (
                  <Typography sx={{ color: 'var(--text-muted)' }}>
                    {tr('Aucune vente pour ce mois.', 'لا توجد مبيعات لهذا الشهر.')}
                  </Typography>
                ) : (
                  <Stack spacing={1.2}>
                    {(() => {
                      const rows = summary?.supplierStats || [];
                      const maxRevenue = Math.max(...rows.map((r) => Number(r.revenue) || 0), 1);
                      return rows.map((row) => (
                        <Box
                          key={`bar-${row.supplier_id || row.supplier_name}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              color: 'var(--text-secondary)',
                              minWidth: 140,
                              fontWeight: 600,
                            }}
                          >
                            {row.supplier_name || tr('Sans fournisseur', 'بدون مورد')}
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              height: 10,
                              borderRadius: 999,
                              background: 'var(--surface-3)',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: `${((Number(row.revenue) || 0) / maxRevenue) * 100}%`,
                                background: 'linear-gradient(90deg, #16a34a, #059669)',
                              }}
                            />
                          </Box>
                          <Typography sx={{ color: 'var(--text-secondary)', minWidth: 90 }}>
                            {formatCurrency(row.revenue, lang)}
                          </Typography>
                        </Box>
                      ));
                    })()}
                  </Stack>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[tr('Fournisseur', 'المورد'), tr('Revenu', 'الإيراد'), tr('Profit', 'الربح'), tr('Quantite vendue', 'الكمية المباعة')].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(summary?.supplierStats || []).map((row) => (
                      <TableRow key={row.supplier_id || row.supplier_name}>
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {row.supplier_name || tr('Sans fournisseur', 'بدون مورد')}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.revenue, lang)}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.profit, lang)}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.soldQuantity}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}








